/**
 * Viewport-bounded feature filtering for the 3D buildings layer.
 *
 * The dashboard renders ~9,900 building footprints via deck.gl. On a
 * tight zoom (e.g. a single street at zoom 18), only ~200-400 of those
 * are inside the visible viewport — yet deck.gl was tessellating all
 * 9,900 every frame. That was the source of the pan/zoom lag.
 *
 * This helper pre-filters to the visible bounding box (with 30% padding
 * so panning a few pixels never reveals a hole) BEFORE passing the
 * FeatureCollection to deck.gl. The re-tessellation cost then scales
 * with what's actually on screen, not the static city size.
 *
 * The math is intentionally approximate: at zoom z, a typical desktop
 * viewport covers roughly visibleDegrees degrees of latitude/longitude.
 * We round the viewport key to 4 decimals (~11m at the equator) so a
 * micro-pan doesn't invalidate the memo and force a layer rebuild.
 *
 * No Mercator projection: the difference between longitude and latitude
 * pixel size at the equator is ~1.0, at Sikhio's latitude (~14.9°N)
 * ~0.97, at zoom 18 ~0.999. Within 3% — fine for "is this building
 * visible" decision.
 */

import type { Feature, FeatureCollection, Polygon, MultiPolygon } from "geojson";
import type { BuildingProperties } from "./building";

/** Approximate degrees-of-latitude visible at the current zoom level. */
function visibleDegrees(zoom: number): number {
  if (zoom >= 18) return 0.0014;
  if (zoom >= 15) return 0.012;
  if (zoom >= 13) return 0.05;
  if (zoom >= 11) return 0.18;
  return 0.5;
}

function featureBbox(
  f: Feature<Polygon | MultiPolygon, BuildingProperties>,
): [[number, number], [number, number]] | null {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  const consider = (lng: number, lat: number) => {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  };
  const visit = (ring: number[][]) => {
    for (const [lng, lat] of ring) consider(lng, lat);
  };
  if (f.geometry.type === "Polygon") {
    for (const ring of f.geometry.coordinates) visit(ring);
  } else {
    for (const poly of f.geometry.coordinates) {
      for (const ring of poly) visit(ring);
    }
  }
  if (!Number.isFinite(minLng)) return null;
  return [[minLng, minLat], [maxLng, maxLat]];
}

/**
 * Filter a building FeatureCollection to those whose bbox overlaps the
 * viewport `[lng-delta, lat-delta] .. [lng+delta, lat+delta]`. With
 * `padding=1.3` (default), the viewport is widened 30% so a few px of
 * pan doesn't reveal a hole.
 *
 * The caller should memoise the result on a viewport key (rounded to
 * ~4 decimals) so micro-pans don't trigger a layer rebuild.
 */
export function filterBuildingsToViewport(
  collection: FeatureCollection<Polygon | MultiPolygon, BuildingProperties>,
  viewport: { longitude: number; latitude: number; zoom: number },
  options: { padding?: number } = {},
): FeatureCollection<Polygon | MultiPolygon, BuildingProperties> {
  const padding = options.padding ?? 1.3;
  const delta = visibleDegrees(viewport.zoom) * padding;
  const minLng = viewport.longitude - delta;
  const maxLng = viewport.longitude + delta;
  const minLat = viewport.latitude - delta;
  const maxLat = viewport.latitude + delta;

  // Cheap bbox overlap test — the bbox of the building has to intersect
  // the viewport bbox. This is correct for 99% of cases; a building
  // whose bbox is entirely outside the viewport is excluded.
  const features: typeof collection.features = [];
  for (const f of collection.features) {
    const bb = featureBbox(f);
    if (!bb) continue;
    const [[fMinLng, fMinLat], [fMaxLng, fMaxLat]] = bb;
    if (fMaxLng < minLng || fMinLng > maxLng) continue;
    if (fMaxLat < minLat || fMinLat > maxLat) continue;
    features.push(f);
  }

  return { type: "FeatureCollection", features };
}

/**
 * Round a viewport's coordinates to a coarse-enough grid that a
 * sub-meter pan doesn't force a layer rebuild. 4 decimals ≈ 11m at
 * the equator; combined with the 30% padding above, a 5px pan is
 * below the rebuild threshold.
 */
export function viewportKey(v: { longitude: number; latitude: number; zoom: number }): string {
  return `${v.longitude.toFixed(4)},${v.latitude.toFixed(4)},${Math.round(v.zoom * 2) / 2}`;
}
